import { useState } from "react";
import toast from "react-hot-toast";
import { upgradeToFullAssessment } from "../api/client";
import LiteReportButton from "../components/LiteReportButton";
import {
  completedFull,
  completedLite,
  isFiveXType,
  upgradeOfferTitle,
} from "../utils/participantAssessmentType";

function ArrowIcon() {
  return (
    <svg
      className="w-5 h-5 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}

export default function CompletedPage({
  variant = "full",
  surveyToken = null,
  fullSurveyToken = null,
  offerLiteReport = true,
  preSurveyPending = false,
  assessmentType,
  branding = null,
  onCompleteBenchmark,
  onOpenFullAssessment,
}) {
  const liteOnly = variant === "lite";
  const [busy, setBusy] = useState(false);
  const fiveX = isFiveXType(assessmentType);

  const handleContinueFull = async () => {
    if (onOpenFullAssessment) {
      setBusy(true);
      try {
        await onOpenFullAssessment();
      } catch (e) {
        toast.error(e.message || "Could not open full assessment");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (fullSurveyToken) {
      window.location.assign(`/a/${fullSurveyToken}`);
      return;
    }
    if (!surveyToken) return;
    setBusy(true);
    try {
      const res = await upgradeToFullAssessment(surveyToken);
      const newToken = res.data?.full_survey_token;
      if (!newToken) {
        toast.error("Could not start full assessment.");
        return;
      }
      window.location.assign(`/a/${newToken}`);
    } catch (e) {
      toast.error(e.message || "Could not start full assessment");
    } finally {
      setBusy(false);
    }
  };

  if (!liteOnly) {
    return (
      <section className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-4 bg-gradient-to-b from-slate-50 to-white">
        {branding?.logo_url ? (
          <img src={branding.logo_url} alt="" className="h-8 w-auto max-w-[140px] object-contain" />
        ) : null}
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 mb-2">
          <svg
            className="w-8 h-8 text-emerald-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-brand mb-2">Thank you</h1>
        <p className="text-gray-700 max-w-md">
          {completedFull(assessmentType)} You may close
          this window.
        </p>
        {preSurveyPending && onCompleteBenchmark ? (
          <button
            type="button"
            onClick={onCompleteBenchmark}
            className="mt-2 inline-flex items-center justify-center rounded-xl border border-[#004f73]/25 bg-white px-6 py-3 text-sm font-semibold text-[#004f73] hover:bg-[#004f73]/5 shadow-sm"
          >
            Complete company benchmark profile
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <section className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-10 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-lg w-full">
        {branding?.logo_url ? (
          <div className="flex justify-center mb-4">
            <img src={branding.logo_url} alt="" className="h-8 w-auto max-w-[140px] object-contain" />
          </div>
        ) : null}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 mb-4 mx-auto">
            <svg
              className="w-8 h-8 text-emerald-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand mb-3">
            {upgradeOfferTitle(assessmentType)}
          </h1>
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            {completedLite(assessmentType)} Continue with the full
            assessment when you are ready
            {offerLiteReport ? ", or review your LITE results first" : ""}.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <article className="relative rounded-2xl border-2 border-brand bg-gradient-to-br from-brand/5 via-white to-brand/[0.07] shadow-lg shadow-brand/10 p-6 sm:p-8">
            <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-brand px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
              Recommended
            </span>
            <h2 className="text-xl font-bold text-brand mt-2 mb-2">
              Continue with the full assessment
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed mb-5">
              {fiveX
                ? "Go deeper across all five pillars. Your LITE answers are already saved."
                : "Go deeper across the remaining assessment areas. Your LITE answers are already saved."}
            </p>
            <button
              type="button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-8 py-3.5 text-base font-semibold text-white shadow-md hover:opacity-90 disabled:opacity-50"
              onClick={handleContinueFull}
              disabled={busy || !surveyToken}
            >
              {busy ? "Opening…" : "Continue with full assessment"}
              {!busy && <ArrowIcon />}
            </button>
          </article>

          {surveyToken && offerLiteReport ? (
            <article className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50/80 p-6 text-left shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-1">
                Your results are ready
              </p>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Check survey results here
              </h2>
              <p className="text-sm text-gray-700 mb-4">
                View your LITE assessment report for early insights on alignment
                and priorities.
              </p>
              <LiteReportButton
                surveyToken={surveyToken}
                label="View LITE assessment report"
                className="w-full inline-flex items-center justify-center rounded-xl border-2 border-amber-400 bg-white px-6 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-50 shadow-sm"
              />
            </article>
          ) : null}
        </div>
      </div>
    </section>
  );
}
